import { normaliseLocale, type CounterLocale } from "@/lib/i18n/config";

/**
 * Where the counter remembers a language choice (ADR-0037, amended for staff profiles).
 *
 * Two keys on this device:
 *
 *   - `pos.locale.staff.<staffId>` — a cache of the signed-in person's own choice, whose home is
 *     `preferredLocale` on their staff profile (GET/PUT /pos/me/preferences). The cache lets the
 *     till open in their language before the edge answers and keeps working if it cannot answer;
 *     the profile is what makes the choice follow them to another till. Keyed by staff id because a
 *     till is shared: the cashier who switched to Hindi at 9am must not hand the manager a Hindi
 *     screen at close.
 *   - `pos.locale.device` — this till's own language, for anyone who signs in without a choice of
 *     their own.
 *
 * Storage can be missing or throw (private window, blocked site data); every access is guarded and
 * the till falls back to the next layer.
 */
const PREFIX = "pos.locale.staff.";
const DEVICE_KEY = "pos.locale.device";
const EVENT = "pos-locale-change";

export function staffLocaleKey(staffId: string): string {
  return `${PREFIX}${staffId}`;
}

export function readStaffLocale(staffId: string | undefined): CounterLocale | undefined {
  if (!staffId || typeof window === "undefined") return undefined;
  try {
    return normaliseLocale(window.localStorage.getItem(staffLocaleKey(staffId)));
  } catch {
    return undefined;
  }
}

/** `undefined` clears the choice, so the business default applies again. */
export function writeStaffLocale(staffId: string, locale: CounterLocale | undefined): void {
  try {
    if (locale) window.localStorage.setItem(staffLocaleKey(staffId), locale);
    else window.localStorage.removeItem(staffLocaleKey(staffId));
  } catch {
    // Storage refused: the choice lasts until the next reload, which is the honest best effort.
  }
  window.dispatchEvent(new Event(EVENT));
}

/** For `useSyncExternalStore`: this tab's writes and other tabs' `storage` events. */
export function subscribeStaffLocale(onChange: () => void): () => void {
  window.addEventListener(EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

export function readDeviceLocale(): CounterLocale | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    return normaliseLocale(window.localStorage.getItem(DEVICE_KEY));
  } catch {
    return undefined;
  }
}

/** `undefined` clears it, so the business default applies to people without their own choice. */
export function writeDeviceLocale(locale: CounterLocale | undefined): void {
  try {
    if (locale) window.localStorage.setItem(DEVICE_KEY, locale);
    else window.localStorage.removeItem(DEVICE_KEY);
  } catch {
    // Storage refused: lasts until the next reload.
  }
  window.dispatchEvent(new Event(EVENT));
}
